import { ScrollReveal } from '@/components/effects/ScrollReveal';
import { GraduationCap, Rocket, ShieldCheck, TrendingUp, ArrowRight } from 'lucide-react';

const services = [
  {
    title: 'Learn',
    subtitle: 'Acodia Learn',
    href: '/learn',
    desc: 'In-demand technology skills via our Active Training Track. All sessions delivered face to face or online — your preference. One to one or small group. Flexible scheduling around your commitments.',
    icon: GraduationCap,
    color: 'var(--violet)',
    tags: ['Active Training Track', 'Expert-Led Sessions'],
  },
  {
    title: 'Launch',
    subtitle: 'Acodia Launch',
    href: '/launch',
    desc: 'Career placement with a job offer within 90 days. CV engineering, interview prep, and direct employer connections.',
    icon: Rocket,
    color: 'var(--info)',
    tags: ['90-Day Target', 'Employer Network'],
  },
  {
    title: 'Defend',
    subtitle: 'Acodia Defend',
    href: '/defend',
    desc: 'Continuous security protection for SMEs. Passive scanning, active monitoring, AI threat summaries, and real-time alerts.',
    icon: ShieldCheck,
    color: 'var(--a)',
    tags: ['Acodia Watch', '24/7 Monitoring'],
  },
  {
    title: 'Reach',
    subtitle: 'Acodia Reach',
    href: '/reach',
    desc: 'Measurable digital growth. SEO, content strategy, paid acquisition, and performance analytics that drive revenue.',
    icon: TrendingUp,
    color: 'var(--ok)',
    tags: ['ROI Focused', 'Data-Driven'],
  },
];

export const ServicesSection = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-14 sm:py-20"
      style={{ background: 'var(--l2)' }}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--a)' }}>
              What We Do
            </p>
            <h2
              data-testid="services-heading"
              className="font-display font-extrabold tracking-[-0.03em] mb-4"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.8rem)', color: 'var(--white)' }}
            >
              Four Areas of Expertise. One Strategic Partner.
            </h2>
            <p className="font-body font-light text-base" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              Direct access to specialist technical expertise. We deliver enterprise-grade security, career development and growth infrastructure without the corporate distance.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
          {services.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <ScrollReveal key={svc.title} delay={i * 0.07}>
                <a
                  href={svc.href}
                  data-testid={`service-card-${svc.title.toLowerCase()}`}
                  className="relative group p-6 sm:p-8 rounded-[10px] transition-all duration-300 cursor-pointer overflow-hidden block"
                  style={{
                    background: 'rgba(10,10,18,0.88)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(139,92,246,0.10)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(139,92,246,0.35)';
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(139,92,246,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(139,92,246,0.10)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Glow overlay */}
                  <div
                    className="absolute top-0 right-0 w-40 h-40 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `radial-gradient(circle, ${svc.color}15, transparent 70%)`, transform: 'translate(20%, -20%) scale(1.2)' }}
                  />

                  <div className="relative z-10">
                    <Icon size={28} style={{ color: svc.color }} className="mb-4" />
                    <p className="font-body text-xs uppercase tracking-[0.22em] mb-1" style={{ color: 'var(--ts)' }}>
                      {svc.subtitle}
                    </p>
                    <h3 className="font-display text-xl font-bold mb-3" style={{ color: 'var(--white)' }}>
                      {svc.title}
                    </h3>
                    <p className="font-body text-sm font-light mb-4" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
                      {svc.desc}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {svc.tags.map((tag) => (
                        <span
                          key={tag}
                          className="font-body text-xs px-2.5 py-0.5 rounded-full"
                          style={{
                            background: `${svc.color}12`,
                            border: `1px solid ${svc.color}25`,
                            color: svc.color,
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div
                      className="flex items-center gap-1 font-body text-sm transition-all duration-200 group-hover:gap-2"
                      style={{ color: svc.color }}
                    >
                      Explore
                      <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </div>
                    {/* Underline */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                      style={{ background: `linear-gradient(90deg, ${svc.color}, transparent)` }}
                    />
                  </div>
                </a>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
